import { memo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { forgotPasswordSchema, type ForgotPasswordFormData } from '../schemas/auth.schema';

interface ForgotPasswordFormProps {
  onSubmit: (email: string, tenantId?: string | null) => Promise<void>;
  onBackToLogin: () => void;
  isLoading: boolean;
  error: string | null;
  /**
   * Tenant resolvido pelo hostname (via /branding). Quando presente, é
   * encaminhado para `onSubmit` para validação cruzada server-side.
   */
  tenantId?: string | null;
}

export const ForgotPasswordForm = memo(function ForgotPasswordForm({
  onSubmit,
  onBackToLogin,
  isLoading,
  error,
  tenantId,
}: ForgotPasswordFormProps) {
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const handleFormSubmit = async (data: ForgotPasswordFormData) => {
    await onSubmit(data.email, tenantId);
    setSent(true);
  };

  if (sent) {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <Mail className="h-7 w-7 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold">Verifique seu e-mail</h3>
          <p className="text-sm text-muted-foreground">
            Enviamos as instruções de recuperação para{' '}
            <span className="font-medium text-foreground">{getValues('email')}</span>
          </p>
        </div>
        <div className="space-y-3">
          <Button variant="outline" className="w-full" onClick={onBackToLogin}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para o login
          </Button>
          <p className="text-xs text-muted-foreground">
            Não recebeu?{' '}
            <button
              type="button"
              onClick={() => setSent(false)}
              className="text-primary hover:underline"
            >
              Reenviar e-mail
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">Esqueceu sua senha?</h3>
        <p className="text-sm text-muted-foreground">
          Informe seu e-mail e enviaremos as instruções para redefinir sua senha.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="forgot-email">E-mail</Label>
          <Input
            id="forgot-email"
            type="email"
            placeholder="seu@email.com"
            autoComplete="email"
            disabled={isLoading}
            {...register('email')}
            className={errors.email ? 'border-destructive' : ''}
          />
          {errors.email && (
            <p className="text-xs text-destructive">{errors.email.message}</p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Enviando...
            </>
          ) : (
            'Enviar instruções'
          )}
        </Button>
      </form>

      <Button
        variant="ghost"
        className="w-full text-muted-foreground"
        onClick={onBackToLogin}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Voltar para o login
      </Button>
    </div>
  );
});
